import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { MetricCard } from "@/components/dashboard/MetricCardV2";

interface Props {
  userId: string;
}

/**
 * Section 1 — Hero stats bar for the shipper dashboard.
 * Four glanceable KPI cards backed by direct table reads (RLS-scoped to the shipper).
 */
export function HeroStats({ userId }: Props) {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["shipper-hero-stats", userId],
    enabled: !!userId,
    staleTime: 60_000,
    queryFn: async () => {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();
      const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59).toISOString();

      // Loads owned by this shipper (id + status only — small list).
      const { data: loads } = await supabase
        .from("direct_loads")
        .select("id, status, awarded_at")
        .eq("created_by", userId);
      const loadList = loads || [];
      const loadIds = loadList.map((l: any) => l.id);

      // Dispatches for those loads.
      const { data: dispatches } = loadIds.length
        ? await supabase
            .from("direct_dispatches")
            .select("id, status, direct_load_id")
            .in("direct_load_id", loadIds)
        : { data: [] as any[] };
      const dispatchList = dispatches || [];

      const posted = loadList.filter((l: any) => l.status === "posted").length;
      const awarded = dispatchList.filter((d: any) => ["assigned", "ready"].includes(d.status)).length;
      const inTransit = dispatchList.filter((d: any) =>
        ["in_transit", "picked_up", "en_route"].includes(d.status),
      ).length;
      const activeLoadCount = posted + awarded + inTransit;

      // Pending bids on this shipper's loads.
      const { data: pendingBids } = loadIds.length
        ? await supabase
            .from("direct_bids")
            .select("id, direct_load_id")
            .in("direct_load_id", loadIds)
            .eq("status", "pending")
        : { data: [] as any[] };
      const pendingBidCount = (pendingBids || []).length;
      const loadsWithPending = new Set((pendingBids || []).map((b: any) => b.direct_load_id)).size;

      // Spend this month / last month — sum accepted bid amounts joined to awarded loads.
      const { data: monthBids } = loadIds.length
        ? await supabase
            .from("direct_bids")
            .select("bid_amount, updated_at, status, direct_load_id")
            .in("direct_load_id", loadIds)
            .eq("status", "accepted")
            .gte("updated_at", startOfLastMonth)
        : { data: [] as any[] };
      const thisMonthSpend = (monthBids || [])
        .filter((b: any) => b.updated_at >= startOfMonth)
        .reduce((sum: number, b: any) => sum + Number(b.bid_amount || 0), 0);
      const lastMonthSpend = (monthBids || [])
        .filter((b: any) => b.updated_at >= startOfLastMonth && b.updated_at <= endOfLastMonth)
        .reduce((sum: number, b: any) => sum + Number(b.bid_amount || 0), 0);

      // Networks + broker count.
      const { data: networks } = await supabase
        .from("shipper_private_networks")
        .select("id")
        .eq("shipper_user_id", userId);
      const networkIds = (networks || []).map((n: any) => n.id);
      let brokerCount = 0;
      if (networkIds.length) {
        const res: any = await supabase
          .from("shipper_network_brokers")
          .select("id", { count: "exact", head: true })
          .in("network_id", networkIds);
        brokerCount = res.count || 0;
      }

      return {
        activeLoadCount,
        posted,
        awarded,
        inTransit,
        pendingBidCount,
        loadsWithPending,
        thisMonthSpend,
        lastMonthSpend,
        networkCount: (networks || []).length,
        brokerCount: brokerCount || 0,
      };
    },
  });

  const fmt = (n: number) =>
    n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const deltaText = (() => {
    if (!data) return "";
    if (!data.lastMonthSpend) return data.thisMonthSpend ? "First month with spend" : "No spend yet";
    const pct = ((data.thisMonthSpend - data.lastMonthSpend) / data.lastMonthSpend) * 100;
    const sign = pct >= 0 ? "+" : "";
    return `vs ${fmt(data.lastMonthSpend)} last (${sign}${pct.toFixed(0)}%)`;
  })();

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 xl:gap-3 [&>*]:h-full">
      <MetricCard
        label="Active Loads"
        value={isLoading ? "—" : String(data?.activeLoadCount ?? 0)}
        sub={
          isLoading
            ? "Loading…"
            : `${data?.posted ?? 0} posted · ${data?.inTransit ?? 0} in transit · ${data?.awarded ?? 0} awarded`
        }
        isLoading={isLoading}
        onClick={() => navigate("/lr-direct/my-loads")}
      />
      <MetricCard
        label="Bids Awaiting Decision"
        value={isLoading ? "—" : String(data?.pendingBidCount ?? 0)}
        sub={isLoading ? "Loading…" : `Across ${data?.loadsWithPending ?? 0} load${data?.loadsWithPending === 1 ? "" : "s"}`}
        isLoading={isLoading}
        onClick={() => navigate("/lr-direct/my-opportunities")}
      />
      <MetricCard
        label="Spend This Month"
        value={isLoading ? "—" : fmt(data?.thisMonthSpend ?? 0)}
        sub={isLoading ? "Loading…" : deltaText}
        isLoading={isLoading}
        onClick={() => navigate("/lr-direct/dispatches")}
      />
      <MetricCard
        label="Private Networks"
        value={isLoading ? "—" : String(data?.networkCount ?? 0)}
        sub={isLoading ? "Loading…" : `${data?.brokerCount ?? 0} broker${data?.brokerCount === 1 ? "" : "s"} total`}
        isLoading={isLoading}
        onClick={() => navigate("/lr-direct/network-settings")}
      />
    </div>
  );
}
